import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Inbox, ArrowLeftRight } from 'lucide-react';
import { Manifest, MANIFEST_TYPE_LABELS, MANIFEST_STATUS_LABELS } from '@/types/manifest.types';

interface Props {
  nodeId: string;
}

export function IncomingManifestsCard({ nodeId }: Props) {
  const { get } = useApi();
  const navigate = useNavigate();
  const [manifests, setManifests] = useState<Manifest[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!nodeId) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res: any = await get(`/api/v1/dashboard/manifests/incoming?node_id=${nodeId}&date=${today}`);
      setManifests(Array.isArray(res) ? res : (res?.data ?? []));
    } catch {
      setManifests([]);
    } finally {
      setLoading(false);
    }
  }, [nodeId, get]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card dir="rtl">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Inbox className="h-5 w-5 text-blue-600" />
          الاستلام اليوم
        </CardTitle>
        <Badge variant="outline">{manifests.length}</Badge>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center text-sm text-muted-foreground py-4">جاري التحميل...</p>
        ) : manifests.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-4">لا توجد قوائم واردة اليوم</p>
        ) : (
          <div className="space-y-2">
            {manifests.map((m) => (
              <div
                key={m.id}
                className="flex items-center justify-between border rounded-lg p-3 hover:bg-muted/40"
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{MANIFEST_TYPE_LABELS[m.manifest_type]}</span>
                    <span className="font-mono text-xs text-muted-foreground">({m.manifest_number})</span>
                    <Badge variant="secondary">{MANIFEST_STATUS_LABELS[m.status]}</Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    السائق: {(m as any).driver?.user?.name || (m as any).driver?.name || 'غير معين'}
                    <span className="mx-2">·</span>
                    الرحلات: {m.completed_trips}/{m.total_trips}
                    {m.failed_trips > 0 && (
                      <>
                        <span className="mx-2">·</span>
                        <span className="text-red-600">تباينات: {m.failed_trips}</span>
                      </>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  className="gap-1"
                  onClick={() => navigate(`/dashboard/fc/${nodeId}/receiving/${m.id}`)}
                >
                  <ArrowLeftRight className="h-4 w-4" />
                  بدء جلسة استلام
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
